import { useEffect } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  pageSectionVisibilityAtom,
  probitPlotChartAtom,
  monthlyVolumesChartAtom,
  ratiosChartAtom,
  tcCrossPlotChartsAtom,
  tcColorByRulesAtom,
  tcInventoryGunBarrelScopeAtom,
} from 'pages/type-curves/type-curve-filters/atoms';
import type {
  PageSectionKey,
  ChartSeriesVisibility,
  RatiosChartVisibility,
  CrossPlotChartConfig,
} from 'pages/type-curves/types';

// Sections collapsed (unchecked) by default on the Inventory Workspace page,
// including the Charts section itself.
const SECTIONS_OFF: PageSectionKey[] = [
  'dcaParameters',
  'summaryTable',
  'charts',
];

const SERIES_HIDDEN: ChartSeriesVisibility = {
  oil: false,
  gas: false,
  water: false,
};
const RATIOS_HIDDEN: RatiosChartVisibility = {
  gor: false,
  wor: false,
  wgr: false,
  cgr: false,
  watercut: false,
};

/**
 * Default view state for the Inventory Workspace page:
 * - DCA Parameters, Summary Table, and Charts sections start unchecked (hidden).
 * - Every chart (probit, monthly volumes, ratios, cross plots) also starts in its
 *   "Hide All" state — no series plotted — so that if the user re-checks the Charts
 *   section, the empty chart containers show and they opt into each series.
 *
 * These are all module-level atoms shared with the plain Type Curve page (where
 * they default on). So the inventory defaults are applied on mount and the prior
 * values restored on unmount, keeping the Type Curve page untouched. Runs once
 * per mount (the setters are stable), so user changes during the session persist.
 *
 * @return {void}
 */
export function useInitInventoryViewDefaults(): void {
  const setVisibility = useSetAtom(pageSectionVisibilityAtom);
  const setProbit = useSetAtom(probitPlotChartAtom);
  const setMonthly = useSetAtom(monthlyVolumesChartAtom);
  const setRatios = useSetAtom(ratiosChartAtom);
  const setCrossPlots = useSetAtom(tcCrossPlotChartsAtom);

  // The Inventory Workspace has NO well-qualifier colouring, in any case. The
  // default rule is already withheld here (usePromotedContextDefaults gates its
  // injection on the inventory scope), but a qualifier rule can still arrive from
  // a saved-rule hydration or a rule duplication. This backstop strips ANY
  // `wellQualifier` rule while the page is scoped to an inventory workspace.
  // Idempotent (returns the same array when nothing matches), so it never loops.
  const inventoryScope = useAtomValue(tcInventoryGunBarrelScopeAtom);
  const [colorByRules, setColorByRules] = useAtom(tcColorByRulesAtom);
  useEffect(() => {
    if (inventoryScope === null) return;
    const filtered = colorByRules.filter(
      (rule) => rule.attribute !== 'wellQualifier'
    );
    if (filtered.length !== colorByRules.length) setColorByRules(filtered);
  }, [inventoryScope, colorByRules, setColorByRules]);

  useEffect(() => {
    let priorSections: Partial<Record<PageSectionKey, boolean>> | null = null;
    let priorProbit: ChartSeriesVisibility | null = null;
    let priorMonthly: ChartSeriesVisibility | null = null;
    let priorRatios: RatiosChartVisibility | null = null;
    let priorCrossPlots: CrossPlotChartConfig[] | null = null;

    setVisibility((prev) => {
      priorSections = Object.fromEntries(
        SECTIONS_OFF.map((key) => [key, prev[key]])
      );
      const next = { ...prev };
      for (const key of SECTIONS_OFF) next[key] = false;
      return next;
    });

    setProbit((prev) => {
      priorProbit = prev;
      return { ...SERIES_HIDDEN };
    });
    setMonthly((prev) => {
      priorMonthly = prev;
      return { ...SERIES_HIDDEN };
    });
    setRatios((prev) => {
      priorRatios = prev;
      return { ...RATIOS_HIDDEN };
    });
    setCrossPlots((prev) => {
      priorCrossPlots = prev;
      return prev.map((chart) => ({ ...chart, visible: false }));
    });

    return () => {
      if (priorSections) {
        setVisibility((prev) => ({ ...prev, ...priorSections }));
      }
      if (priorProbit) setProbit(priorProbit);
      if (priorMonthly) setMonthly(priorMonthly);
      if (priorRatios) setRatios(priorRatios);
      if (priorCrossPlots) setCrossPlots(priorCrossPlots);
    };
  }, [setVisibility, setProbit, setMonthly, setRatios, setCrossPlots]);
}
